import React, { useState, useEffect, useContext } from "react";
import { Layout } from "@/components/Layout";
import { API, AuthContext } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, Mail } from "lucide-react";

export default function AdminUsersPage() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";

  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [toRemove, setToRemove] = useState(null);

  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/allowed-emails`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setEmails(data.emails || []);
    } catch {
      toast.error("Kunne ikke hente whitelisten");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Indtast en gyldig email-adresse");
      return;
    }
    setAdding(true);
    try {
      const res = await fetch(`${API}/admin/allowed-emails`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Kunne ikke tilføje email");
      }
      toast.success(`Tilføjet: ${email}`);
      setNewEmail("");
      fetchEmails();
    } catch (err) {
      toast.error(err.message || "Fejl ved tilføjelse");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async () => {
    if (!toRemove) return;
    try {
      const res = await fetch(
        `${API}/admin/allowed-emails/${encodeURIComponent(toRemove)}`,
        { method: "DELETE", credentials: "include" }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Kunne ikke fjerne email");
      }
      toast.success(`Fjernet: ${toRemove}`);
      fetchEmails();
    } catch (err) {
      toast.error(err.message || "Fejl ved fjernelse");
    } finally {
      setToRemove(null);
    }
  };

  if (!isAdmin) {
    return (
      <Layout title="Admin Users">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">
              Du skal være admin for at administrere whitelisten.
            </p>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout title="Admin Users">
      <div className="max-w-3xl space-y-6" data-testid="admin-users-page">
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-amber-700" />
              <CardTitle className="text-amber-900">Flow er kilde til admin-adgang</CardTitle>
            </div>
            <CardDescription className="text-amber-900/80">
              Owntune kontrollerer adgang via VinylFlow ved login. Brugere med rollen{" "}
              <strong>admin</strong> eller <strong>super_admin</strong> i Flow får automatisk adgang.
              Hvis Flow ikke kan nås, bruges en cachet rolle (gyldig i 24 timer) — eller listen nedenfor
              som nødfallback.
            </CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <CardTitle>Fallback Whitelist (nødadgang)</CardTitle>
            </div>
            <CardDescription>
              Brug kun denne liste til teknisk/nødadgang når Flow er nede. Normal admin-administration sker i VinylFlow.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdd} className="flex gap-2 mb-6">
              <Input
                type="email"
                placeholder="navn@firma.dk"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={adding}
                data-testid="new-email-input"
                className="flex-1"
              />
              <Button
                type="submit"
                disabled={adding || !newEmail.trim()}
                data-testid="add-email-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {adding ? "Tilføjer..." : "Tilføj"}
              </Button>
            </form>

            {loading ? (
              <div className="text-sm text-muted-foreground py-8 text-center">
                Indlæser...
              </div>
            ) : emails.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">
                Ingen emails på whitelisten endnu.
              </div>
            ) : (
              <div
                className="border rounded-md divide-y"
                data-testid="allowed-emails-list"
              >
                {emails.map((e) => {
                  const isSelf = e.email === (user?.email || "").toLowerCase();
                  return (
                    <div
                      key={e.email}
                      className="flex items-center justify-between px-4 py-3 hover:bg-muted/40"
                      data-testid={`email-row-${e.email}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium truncate">{e.email}</div>
                          <div className="text-xs text-muted-foreground">
                            Tilføjet af {e.added_by || "—"}
                            {e.created_at && ` · ${new Date(e.created_at).toLocaleDateString("da-DK")}`}
                          </div>
                        </div>
                        {isSelf && (
                          <Badge variant="secondary" className="ml-2">Dig</Badge>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={isSelf}
                        onClick={() => setToRemove(e.email)}
                        data-testid={`remove-email-btn-${e.email}`}
                        title={isSelf ? "Du kan ikke fjerne dig selv" : "Fjern"}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-xs text-muted-foreground mt-4">
              Tip: Tilføj/fjern almindelige brugere i VinylFlow. Denne liste skal kun bruges hvis Flow er nede.
            </p>
          </CardContent>
        </Card>
      </div>

      <AlertDialog
        open={!!toRemove}
        onOpenChange={(o) => !o && setToRemove(null)}
      >
        <AlertDialogContent data-testid="remove-email-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Fjern email fra whitelisten?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium">{toRemove}</span> vil miste adgang ved næste
              login. Du kan altid tilføje den igen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-cancel-btn">Annuller</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="remove-confirm-btn"
            >
              Fjern
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
